import {
  Button,
  Input,
  Modal,
  StatusPill,
  formatCivilDate,
  formatCurrency,
} from '@hue/design-system';
import { useState } from 'react';
import { getApiError, type DuplicateSuspect } from '../../lib/api.js';
import { RequestAlert } from '../../app/data-view.js';

/// Semelhança não recusa o lançamento: duas salas podem ter o mesmo aluguel. A tela mostra a quem
/// ele se parece e cobra uma escolha — adotar o que já existe ou manter os dois com motivo.
export function DuplicateSuspicionDialog({
  suspects,
  title,
  description,
  confirmLabel,
  onLink,
  onConfirm,
  onClose,
}: {
  suspects: DuplicateSuspect[];
  title: string;
  description: string;
  confirmLabel: string;
  onLink?: ((suspect: DuplicateSuspect) => Promise<void>) | undefined;
  onConfirm: (reason: string) => Promise<void>;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await action();
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setBusy(false);
    }
  }
  async function confirm() {
    if (reason.trim().length < 3) {
      setError('Explique por que os dois lançamentos devem existir.');
      return;
    }
    await run(() => onConfirm(reason));
  }
  return (
    <Modal open onOpenChange={onClose} title={title} description={description}>
      <ul className="space-y-2" aria-label="Lançamentos parecidos">
        {suspects.map((suspect) => (
          <li
            key={`${suspect.kind}:${suspect.id}`}
            className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-surface-border bg-surface-soft p-4"
          >
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <strong>{suspect.description}</strong>
                <StatusPill tone="neutral">
                  {suspect.kind === 'RECURRENCE' ? 'Recorrência' : 'Já lançada'}
                </StatusPill>
              </div>
              <p className="mt-1 text-sm text-content-muted">
                {suspect.counterpartyName} · {detailOf(suspect)}
              </p>
            </div>
            {suspect.linkable && onLink ? (
              <Button
                size="sm"
                variant="outline"
                loading={busy}
                onClick={() => void run(() => onLink(suspect))}
              >
                Vincular esta despesa
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      <div className="mt-4">
        <Input
          label="Por que os dois devem existir?"
          value={reason}
          maxLength={240}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      {error ? <RequestAlert message={error} /> : null}
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          Voltar
        </Button>
        <Button loading={busy} onClick={() => void confirm()}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

function detailOf(suspect: DuplicateSuspect): string {
  const amount =
    suspect.amountCents === null
      ? 'valor informado todo mês'
      : formatCurrency(suspect.amountCents, suspect.currency);
  return suspect.dueOn
    ? `vence em ${formatCivilDate(suspect.dueOn)} · ${amount}`
    : `vence todo dia ${String(suspect.dueDay)} · ${amount}`;
}
